import Avatar from '@pelckmans/elementis/components/avatar';
import Breadcrumbs from '@pelckmans/elementis/components/breadcrumbs';
import Button from '@pelckmans/elementis/components/button';
import Page from '@pelckmans/elementis/components/page';
import PillGroup from '@pelckmans/elementis/components/pill-group';
import SidebarIcon from '@pelckmans/elementis/icons/sidebar';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExerciseNode } from '../../../../../api/exercise-node';
import { Node } from '../../../../../api/table-of-content';
import SublevelSummary from '../../../../../components/module/SublevelSummary';
import { findDeepNodeById } from '../../../../../utils/tocNode';
import { useExerciseModuleContext } from '../../../_contexts/ExerciseModuleContext';
import { MaterialDataContext } from '../../../_contexts/MaterialDataProvider';
import ModuleContext from '../../../_contexts/ModuleContext';
import SidebarVisibilityContext from '../../../_contexts/SidebarVisibilityContext';
import SubModuleBreadcrumb from '../../../components/breadcrumbs/SubModuleBreadcrumb';
import SuperModuleBreadcrumb from '../../../components/breadcrumbs/SuperModuleBreadcrumb';
import SuperModuleLevelPageBreadcrumb from '../../../components/breadcrumbs/SuperModuleLevelPageBreadcrumb';
import TableOfContentsContext from '../../_contexts/TableOfContentsContext';
import AppButtonList from './AppButtonList';
import NodeHierarchyBreadcrumbs from './NodeHierarchyBreadcrumbs';

const findCurrentExerciseNode = (exerciseNodes: ExerciseNode[], selectedNodeId: string): ExerciseNode | undefined => {
  for (const node of exerciseNodes) {
    if (node.id === selectedNodeId) return node;

    if (node.nodes && node.nodes.length > 0) {
      const found = findCurrentExerciseNode(node.nodes, selectedNodeId);
      if (found) return found;
    }
  }
};

interface TocPageHeaderProps {
  density: 'normal' | 'compact';
}

function TocPageHeader({ density }: TocPageHeaderProps) {
  const { t } = useTranslation();
  const { module } = useContext(ModuleContext);
  const { selectedNode, nodes } = useContext(TableOfContentsContext);
  const { selectedNodeIds } = useContext(MaterialDataContext);
  const { setSidebarVisibility } = useContext(SidebarVisibilityContext);
  const { exerciseNodes, isLoading } = useExerciseModuleContext();

  if (!module || !selectedNode) return null;

  const currentExerciseNode = findCurrentExerciseNode(exerciseNodes, selectedNode.id);

  const nodeHierarchy = selectedNodeIds.map(id => findDeepNodeById(nodes, id)) as Node[];

  return (
    <Page.Header
      density={density}
      headerText={selectedNode.id === 'general' ? t('TOC.GENERAL') : selectedNode.title}
      cover={{
        backgroundImageUrl: selectedNode?.background || module.styling?.visual,
        custom: Boolean(selectedNode?.background),
      }}
      meta={
        currentExerciseNode &&
        !isLoading && (
          <PillGroup role="group" aria-label={t('PAGES.VEM.HEADER.SUBLEVEL_SUMMARY_A11Y_LABEL')}>
            <SublevelSummary
              sublevels={currentExerciseNode?.nodes}
              totalExercises={currentExerciseNode?.exerciseCount || 0}
              completedExercises={currentExerciseNode?.completedExerciseCount}
              score={currentExerciseNode?.score}
            />
          </PillGroup>
        )
      }
      extra={<AppButtonList density={density} />}
      breadcrumbs={
        <Breadcrumbs aria-label={t('PAGES.TOC_LEVEL.SIDEBAR.BREADCRUMB.A11Y_LABEL')}>
          <SuperModuleBreadcrumb />
          <SuperModuleLevelPageBreadcrumb />
          <SubModuleBreadcrumb />
          <NodeHierarchyBreadcrumbs nodeHierarchy={nodeHierarchy} />
        </Breadcrumbs>
      }
      contextButton={
        <Button
          startIcon={<SidebarIcon />}
          onClick={() => {
            setSidebarVisibility(true);
          }}
        >
          {t('PAGES.TOC_LEVEL.PAGE_HEADER.CONTEXT_BUTTON.OPEN_SIDEBAR')}
        </Button>
      }
      prefix={
        selectedNode.prefix && selectedNode.displayPrefix
          ? {
              text: selectedNode.prefix,
              avatarImage: selectedNode.image ? (
                <Avatar.Image name={selectedNode.prefix} src={selectedNode.image} />
              ) : undefined,
            }
          : undefined
      }
    />
  );
}

export default TocPageHeader;
